import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { advertisers } from "../api/advertisers";
import { ApiError } from "../api/client";

export function AdvertiserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    setDraft(null);
    setMessage(null);
  }, [id]);

  const detail = useQuery({
    queryKey: ["advertiser", id],
    queryFn: () => advertisers.get(id!),
    enabled: Boolean(id),
  });

  const audit = useQuery({
    queryKey: ["advertiser", id, "audit"],
    queryFn: () => advertisers.audit(id!),
    enabled: Boolean(id),
  });

  const save = useMutation({
    mutationFn: (displayName: string) => advertisers.update(id!, {
      displayName,
      legalName: detail.data!.legalName,
      legalMetadata: JSON.stringify(detail.data!.legalMetadata),
    }),
    onSuccess: async () => {
      setMessage("Сохранено");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["advertiser", id] }),
        queryClient.invalidateQueries({ queryKey: ["advertisers"] }),
        queryClient.invalidateQueries({ queryKey: ["advertiser", id, "audit"] }),
      ]);
    },
    onError: (error) => {
      setMessage(error instanceof ApiError ? `Не сохранено: ${error.code}` : "Не сохранено");
    },
  });

  if (!id) return <p className="error">Не указан идентификатор.</p>;
  if (detail.isLoading) return <p className="muted">Загружаю…</p>;
  if (detail.isError) {
    if (detail.error instanceof ApiError && detail.error.status === 404) {
      return <p className="error">Рекламодатель не найден.</p>;
    }
    return (
      <p className="error" role="alert">
        Не удалось загрузить рекламодателя.{" "}
        <button type="button" onClick={() => void detail.refetch()}>Повторить</button>
      </p>
    );
  }
  if (!detail.data) return null;

  const value = draft ?? detail.data.displayName;

  function submit(event: FormEvent) {
    event.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    setMessage(null);
    save.mutate(trimmed);
  }

  return (
    <>
      <p className="muted">
        <Link to="/advertisers">← Рекламодатели</Link>
      </p>
      <h2>{detail.data.displayName}</h2>

      <form className="panel" onSubmit={submit} style={{ marginBottom: 16 }}>
        <div className="field">
          <label htmlFor="name">Название</label>
          <input id="name" value={value} onChange={(e) => setDraft(e.target.value)} />
        </div>
        <p className="muted">
          Статус: {detail.data.status} · версия {detail.data.version}
        </p>
        {message && <p className={message === "Сохранено" ? "muted" : "error"}>{message}</p>}
        <button className="primary" type="submit" disabled={save.isPending || value.trim() === detail.data.displayName}>
          {save.isPending ? "Сохраняю…" : "Сохранить"}
        </button>
      </form>

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Аудит</h3>
        {audit.isLoading && <p className="muted">Загружаю…</p>}
        {audit.isError && (
          <p className="error" role="alert">
            Не удалось загрузить аудит.{" "}
            <button type="button" onClick={() => void audit.refetch()}>Повторить</button>
          </p>
        )}
        {audit.data && audit.data.length === 0 && <p className="muted">Событий нет.</p>}
        {audit.data && audit.data.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Действие</th>
                <th>Автор</th>
                <th>Когда</th>
              </tr>
            </thead>
            <tbody>
              {audit.data.map((e) => (
                <tr key={e.id}>
                  <td>{e.operation}</td>
                  <td>admin</td>
                  <td>{new Date(e.occurredAt).toLocaleString("ru-RU")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
